import { useMemo } from 'react'

export type TabSwitchOption<T = string | number> = {
  label: string | JSX.Element
  value: T
}

export type TabSwitchProps = {
  id: string
  options?: Array<TabSwitchOption>
  onChange: (e: React.ChangeEvent<any>) => void
  className?: string
  disabled?: boolean
  value?: string
  color?: 'primary' | 'dark' | 'green' | 'forest' | 'lime'
  size?: 'large' | 'small'
}

const TabSwitch = ({
  id,
  options,
  onChange,
  disabled,
  className = '',
  color = 'primary',
  size = 'large',
  value
}: TabSwitchProps) => {
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e)
    }
  }

  const containerColorClasses = useMemo(() => {
    let colorStyles = ''
    switch (color) {
      case 'dark':
        colorStyles = 'border-charcoal'
        break
      case 'green':
        colorStyles = 'border-mostlyGreen'
        break
      case 'forest':
        colorStyles = 'border-forestDeep'
        break
      case 'lime':
        colorStyles = 'border-transparent bg-limeFresh/50'
        break
      case 'primary':
      default:
        colorStyles = 'border-primary'
        break
    }
    return colorStyles
  }, [color])

  const inputColorClasses = useMemo(() => {
    const baseClasses = 'peer-checked:text-white peer-checked:cursor-default'
    let colorStyles = ''

    switch (color) {
      case 'dark':
        colorStyles = `${baseClasses} peer-checked:bg-charcoal text-oliveAsh hover:bg-iron`
        break
      case 'green':
        colorStyles = `${baseClasses} peer-checked:bg-mostlyGreen text-ashGrey hover:bg-superSilver hover:text-steelGrey`
        break
      case 'forest':
        colorStyles = `${baseClasses} peer-checked:bg-forestDeep peer-checked:hover:bg-forestDeep text-forestDeep hover:bg-limeBright/30`
        break
      case 'lime':
        colorStyles =
          'peer-checked:cursor-default peer-checked:bg-forestDeep peer-checked:text-limeFresh peer-checked:hover:bg-forestDeep text-forestDeep hover:bg-forestDeep/10'
        break
      case 'primary':
      default:
        colorStyles = `${baseClasses} peer-checked:bg-primary text-charcoal hover:bg-lightPink`
        break
    }

    return colorStyles
  }, [color])

  const containerSizeClasses = useMemo(() => {
    let sizeStyles = ''
    switch (size) {
      case 'large':
        sizeStyles = 'h-14 text-lg font-bold p-1'
        break
      case 'small':
      default:
        sizeStyles = 'h-9 text-sm font-medium p-0.5'
        break
    }
    return sizeStyles
  }, [size])

  const inputSizeClasses = useMemo(() => {
    let sizeStyles = ''
    switch (size) {
      case 'large':
        sizeStyles = 'text-lg font-bold px-5 py-[10px]'
        break
      case 'small':
      default:
        sizeStyles = 'text-sm leading-none font-medium px-[15px] py-2'
        break
    }
    return sizeStyles
  }, [size])

  const getCornerClasses = (idx: number, itemCount: number) => {
    switch (true) {
      case idx === 0:
        return 'rounded-l-full'
      case idx === itemCount - 1:
        return 'rounded-r-full'
      default:
        return ''
    }
  }

  if (!options) return null

  return (
    <div
      role='radiogroup'
      aria-labelledby={id}
      className={`flex flex-row overflow-hidden border rounded-full ${containerColorClasses} ${containerSizeClasses} ${className}`}
    >
      {options.map((option, idx, optionsArr) => (
        <div
          key={`${id}_${option.value}`}
          className={`${getCornerClasses(
            idx,
            optionsArr.length
          )} transition-colors duration-200 ease-in-out flex-1 select-none ${
            color === 'lime' ? 'bg-transparent' : 'bg-white'
          }`}
        >
          <input
            id={`${id}_${option.value}`}
            className='peer sr-only'
            name={id}
            onChange={handleOnChange}
            type='radio'
            value={option.value}
            disabled={disabled}
            checked={option.value === value}
          />
          <label
            htmlFor={`${id}_${option.value}`}
            className={`transition-colors duration-200 ease-in-out rounded-full peer-disabled:opacity-40 peer-disabled:cursor-default h-full flex items-center justify-center cursor-pointer text-center ${inputSizeClasses} ${inputColorClasses}`}
          >
            {option.label}
          </label>
        </div>
      ))}
    </div>
  )
}

export default TabSwitch
